import { Components, Theme } from "@mui/material";
import { disabledContainedButtonSx } from "./buttonStyles";
import COLORS from "./colors";

export const components: Components<Omit<Theme, "components">> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundImage: COLORS.appShellBackground,
        backgroundAttachment: "fixed",
        backgroundColor: COLORS.gradients.sidebar.bottom,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover"
      },
      "#root": {
        minHeight: "100dvh"
      }
    }
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: "transparent",
        backgroundImage: "none",
        border: 0,
        boxShadow: "none"
      }
    }
  },
  MuiTooltip: {
    defaultProps: {
      placement: "top"
    },
    styleOverrides: {
      tooltip: ({ theme: { palette } }) => ({
        backgroundColor: palette.secondary.main
      })
    }
  },
  MuiCircularProgress: {
    defaultProps: {
      size: "17px",
      color: "secondary"
    }
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: "white",
        textDecoration: "none",
        ":visited": {
          color: "inherit"
        }
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        marginBlock: 2,
        borderRadius: COLORS.radius.md,
        paddingBlock: 10,
        paddingInline: 20,
        fontWeight: 500,
        textTransform: "none",
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none"
        }
      },
      containedPrimary: {
        backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
        color: COLORS.white,
        ":hover": {
          backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
          filter: "brightness(1.05)"
        },
        "&.Mui-disabled": disabledContainedButtonSx
      },
      outlinedPrimary: ({ theme: { palette } }) => ({
        borderColor: palette.text.primary,
        color: palette.text.primary,
        borderRadius: COLORS.radius.md,
        ":hover": {
          backgroundColor: "rgba(255,255,255,0.08)",
          borderColor: palette.text.primary,
          color: palette.text.primary
        }
      })
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: COLORS.radius.lg,
        boxShadow: COLORS.shadow.card
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: COLORS.radius.lg
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: COLORS.radius.pill,
        fontWeight: 500,
        fontSize: 12
      }
    }
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          color: palette.custom.silver
        };
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          color: palette.custom.silver,
          "&.Mui-focused": {
            color: palette.text.primary
          }
        };
      },
      standard: ({ theme }) => {
        return {
          color: theme.palette.text.disabled,
          "&.Mui-focused": {
            color: theme.palette.text.disabled
          }
        };
      }
    }
  },
  MuiInput: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          color: theme.palette.text.secondary,
          "&:before": {
            borderColor: theme.palette.text.secondary
          },
          "&:hover:not(.Mui-disabled):before": {
            borderColor: theme.palette.text.secondary
          },
          "&:after": {
            borderColor: theme.palette.text.secondary
          }
        };
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 0 ${palette.custom.silver} inset`, // Fixed
            WebkitTextFillColor: "#fff", // Fixed
            WebkitBackgroundColor: "#000" // Fixed
          }
        };
      },
      root: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          borderColor: palette.custom.silver,
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.custom.prelude
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.custom.prelude
            }
          }
        };
      }
    }
  },
  MuiFilledInput: {
    styleOverrides: {
      input: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 0 ${palette.custom.silver} inset`, // Fixed
            WebkitTextFillColor: "#fff", // Fixed
            WebkitBackgroundColor: "#000" // Fixed
          }
        };
      },
      root: ({ theme }) => {
        let palette: any = theme.palette;
        return {
          color: palette.secondary.main,
          "&:hover:not(.Mui-disabled):before": {
            borderColor: palette.text.primary
          },
          "&:before": {
            borderColor: palette.text.primary
          },
          "&:after": {
            borderColor: palette.text.primary
          }
        };
      }
    }
  },
  MuiMenu: {
    styleOverrides: {
      list: ({ theme }) => {
        const palette = theme.palette as any;
        return {
          backgroundColor: "white",
          color: palette.text.secondary
        };
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: COLORS.secondary,
        // Theme is dark-mode globally, but menus use a white surface — dark-mode
        // hover (white @ 8%) is invisible on white. Force readable light-surface states.
        "&:hover": {
          backgroundColor: "rgba(59, 132, 243, 0.12)"
        },
        "&.Mui-focusVisible": {
          backgroundColor: "rgba(59, 132, 243, 0.16)"
        },
        "&.Mui-selected": {
          backgroundColor: "rgba(59, 132, 243, 0.18)",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "rgba(59, 132, 243, 0.24)"
          }
        }
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      title: () => ({
        color: COLORS.surface.primaryText
      })
    }
  },
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme: { palette } }) => {
        return {
          color: palette.text.secondary,
          borderBottom: "none"
        };
      }
    }
  }
};

export default components;
